<template>
  <div>
    <div v-if="isGoogleMaps" class="mb-6">
      <GoogleMaps
        :latitude="newAddress.latitude ? Number(newAddress.latitude) : 0"
        :longitude="newAddress.longitude ? Number(newAddress.longitude) : 0"
        @hide-google-maps="isGoogleMaps = false"
        @save-coordinates="saveCoordinates"
      />
    </div>
    <div v-show="!isGoogleMaps">
      <div class="mb-3">
        <div v-if="isManualMode">
          <BaseInput
            v-model="newAddress.addressLine1"
            :label="$t('delivery.labels.addressManual')"
            type="text"
            :required="true"
            :class="{ 'border border-red-500': fieldErrors?.addressLine1 || fieldErrorsProps?.addressLine1 }"
          />
        </div>
        <BaseInput
          v-else
          id="address1"
          v-model="newAddress.addressLine1"
          label="Address Search"
          type="text"
          :required="true"
          :class="{ 'border border-red-500': fieldErrors?.addressLine1 || fieldErrorsProps?.addressLine1 }"
        />
        <div class="text-right mt-3 opacity-50">
          <button
            class="cursor-pointer hover:underline"
            v-if="!isFullForm"
            @click="((isFullForm = true), (isManualMode = true))"
          >
            {{ $t('delivery.labels.enterAddressManually') }}
          </button>
        </div>
      </div>
      <div v-show="isFullForm">
        <div class="mb-3 flex gap-3">
          <div class="flex-1">
            <BaseInput
              id="address2"
              v-model="newAddress.addressLine2"
              :label="$t('delivery.labels.address2')"
              type="text"
            />
          </div>
          <div class="flex-1">
            <BaseInput
              id="address3"
              v-model="newAddress.addressLine3"
              :label="$t('delivery.labels.address3')"
              type="text"
            />
          </div>
        </div>
        <div class="mb-3 flex gap-3">
          <div class="flex-1">
            <BaseInput id="town" v-model="newAddress.addressTown" :label="$t('delivery.labels.town')" type="text" />
          </div>
          <div class="flex-1">
            <BaseInput
              id="county"
              v-model="newAddress.addressCounty"
              :label="$t('delivery.labels.county')"
              type="text"
            />
          </div>
        </div>
        <div class="mb-3 flex gap-3">
          <div class="flex-1">
            <CountrySelect
              v-model:address-iso="newAddress.addressIso"
              :class="{ 'border border-red-500': fieldErrors?.addressIso || fieldErrorsProps?.addressIso }"
            />
          </div>
          <div class="flex-1">
            <BaseInput
              id="postcode"
              v-model="newAddress.addressCode"
              :label="$t('delivery.labels.postcode')"
              type="text"
              :required="true"
              :class="{ 'border border-red-500': fieldErrors?.addressCode || fieldErrorsProps?.addressCode }"
            />
          </div>
        </div>
        <div v-if="isLatLngRequired" class="mb-3 flex gap-3">
          <div class="flex-1">
            <BaseInput
              id="latitude"
              v-model.number="newAddress.latitude"
              :label="$t('delivery.labels.latitude')"
              type="text"
            />
          </div>
          <div class="flex-1">
            <BaseInput
              id="longitude"
              v-model.number="newAddress.longitude"
              :label="$t('delivery.labels.longitude')"
              type="text"
            />
          </div>
        </div>
        <div class="mb-3 flex gap-3" v-if="isPrimaryToggle">
          <div class="flex-1 flex flex-col gap-3 border rounded">
            <Toggle
              class="p-2"
              :label="$t('delivery.labels.primaryAddress')"
              :active="newAddress.isPrimary"
              @toggle="newAddress.isPrimary = $event"
            />
          </div>
          <div v-if="isLatLngRequired" class="flex-1 opacity-50 ml-auto text-right">
            <button @click="isGoogleMaps = true" class="hover:underline cursor-pointer">
              {{ $t('delivery.labels.adjustCoordinates') }}
            </button>
          </div>
        </div>
        <div v-if="isSaveButton" class="text-right mt-10" :class="{ 'pointer-events-none opacity-50': !isChanged }">
          <button class="btn btn-action btn-lg rounded-sm" @click.prevent="saveAddress">Save Changes</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CountrySelect from '@/components/shared/address/CountrySelect.vue'
import GoogleMaps from '@/components/shared/address/GoogleMaps.vue'
import Toggle from '@/components/shared/ui/Toggle.vue'
import { useAddressLookup } from '@/composables/addressLookup'
import { useValidation, validators } from '@/composables/validation'
import alertbox from '@/utils/alertbox'
import { config } from '@/utils/config'

const validationRules = {
  addressLine1: [validators.required('delivery.labels.address1'), validators.maxLength(64, 'delivery.labels.address1')],
  addressIso: [validators.required('delivery.labels.country'), validators.maxLength(3, 'delivery.labels.country')],
  addressCode: [validators.required('delivery.labels.postcode'), validators.maxLength(16, 'delivery.labels.postcode')]
}

export default {
  components: {
    CountrySelect,
    GoogleMaps,
    Toggle
  },
  props: {
    isPrimaryByDefault: {
      type: Boolean,
      default: true
    },
    isLatLngRequired: {
      type: Boolean,
      default: false
    },
    isSaveButton: {
      type: Boolean,
      default: false
    },
    isPrimaryToggle: {
      type: Boolean,
      default: true
    },
    fieldErrorsProps: Object,
    address: Object
  },
  setup() {
    const { setupAddressLookup } = useAddressLookup(config.environment)
    const { fieldErrors, validate } = useValidation(validationRules)
    return {
      fieldErrors,
      validate,
      setupAddressLookup
    }
  },
  data() {
    return {
      newAddress: {
        addressLine1: '',
        addressLine2: '',
        addressLine3: '',
        addressTown: '',
        addressCounty: '',
        addressCode: '',
        addressIso: 'GB',
        latitude: undefined,
        longitude: undefined,
        isPrimary: true
      },
      originalAddress: {},
      isGoogleMaps: false,
      isFullForm: false,
      isManualMode: false
    }
  },
  created() {
    if (this.address) {
      const addressMapped = {
        addressLine1: this.address.line1,
        addressLine2: this.address.line2,
        addressLine3: this.address.line3,
        addressTown: this.address.town,
        addressCounty: this.address.county,
        addressCode: this.address.code,
        addressIso: this.address.iso,
        isPrimary: this.address.isPrimary,
        latitude: this.address.latitude,
        longitude: this.address.longitude
      }
      this.originalAddress = JSON.parse(JSON.stringify(addressMapped))
      this.newAddress = JSON.parse(JSON.stringify(this.originalAddress))
      this.isFullForm = true
    } else {
      this.newAddress.isPrimary = this.isPrimaryByDefault
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.setUpPredictiveAddresses()
    })
    this.$watch('newAddress', this.updateAddress, { deep: true })
  },
  computed: {
    isChanged() {
      return JSON.stringify(this.newAddress) !== JSON.stringify(this.originalAddress)
    }
  },
  methods: {
    updateAddress(address) {
      this.$emit('address-updated', address)
    },
    saveCoordinates(latitude, longitude) {
      this.newAddress.latitude = latitude
      this.newAddress.longitude = longitude
    },
    async setUpPredictiveAddresses() {
      this.setupAddressLookup('address1', (address) => {
        this.isFullForm = true
        try {
          this.newAddress.latitude = address.RawAddress.Location.Latitude
          this.newAddress.longitude = address.RawAddress.Location.Longitude
        } catch {
          this.newAddress.latitude = undefined
          this.newAddress.longitude = undefined
        }
      })
    },
    async saveAddress() {
      const errors = await this.validate({
        addressLine1: this.newAddress.addressLine1,
        addressIso: this.newAddress.addressIso,
        addressCode: this.newAddress.addressCode
      })

      if (errors) return

      if ((!this.newAddress.latitude || !this.newAddress.longitude) && this.isLatLngRequired) {
        const { isConfirmed } = await alertbox({
          text: this.$t('delivery.validation.latitudeLongitudeRequired'),
          buttons: [
            { text: this.$t('buttons.cancel'), style: 'btn-secondary', action: { isConfirmed: false } },
            { text: this.$t('buttons.continue'), style: 'btn-action', action: { isConfirmed: true } }
          ]
        })
        if (isConfirmed) {
          this.$emit('saveAddress')
        }
      } else {
        this.$emit('saveAddress')
      }
    }
  }
}
</script>
